/**
 * CostDetailModal 費用明細 Modal
 *
 * 顯示單一等級的費用明細：
 * - 規劃概要
 * - 費用項目明細表格
 * - 總價顯示
 */

import { Modal } from '@/components/layout/Modal';
import type { CostBreakdown, CostBreakdownByLevel, CostItemDetail } from '@/types/api';
import { escapeHtml } from '@/utils/security';

// ========================================================================
// 型別定義
// ========================================================================

export type CostLevel = 'simple' | 'standard' | 'premium';

interface CostDetailModalOptions {
  // 選項預留
}

// 產品連結定義
const PRODUCT_LINKS: Record<string, { name: string; url: string }> = {
  // 簡易版和標準版共用
  employee_desk_basic: {
    name: 'Backbone Dyback Lite 電動升降桌含桌墊',
    url: 'https://www.backbone.tw/products/dybacklite',
  },
  employee_chair_basic: {
    name: 'Backbone Gull工學椅海鷗翱翔款',
    url: 'https://www.backbone.tw/products/gull',
  },
  // 高級版
  employee_desk_premium: {
    name: 'Backbone Allround desk 電動升降桌',
    url: 'https://www.backbone.tw/products/allroundbundle',
  },
  employee_chair_premium: {
    name: 'Backbone Eagle 人體工學椅',
    url: 'https://www.backbone.tw/products/eagle',
  },
  // 所有等級共用
  supervisor_desk: {
    name: 'Backbone Starship 電動升降工作站',
    url: 'https://www.backbone.tw/products/starship',
  },
  supervisor_chair: {
    name: 'Backbone King Kong 金剛人體工學旗艦椅',
    url: 'https://www.backbone.tw/products/king-kong',
  },
  supervisor_tv: {
    name: 'Xiaomi 智慧顯示器 A 2026 65型 4K',
    url: 'https://www.mi.com/tw/product/xiaomi-tv-a-65-2026/',
  },
  large_meeting_table: {
    name: '會議桌(4張合併)',
    url: 'https://www.ikea.com.tw/zh/products/office-workspace/office-desks-and-tables/mittzon-spr-99533396',
  },
  large_meeting_chair: {
    name: 'Backbone Axo 人體工學辦公椅',
    url: 'https://www.backbone.tw/products/axo',
  },
  large_meeting_tv: {
    name: 'Xiaomi 智慧顯示器 A 2026 65型 4K',
    url: 'https://www.mi.com/tw/product/xiaomi-tv-a-65-2026/',
  },
  medium_meeting_table: {
    name: '會議桌(2張合併)',
    url: 'https://www.ikea.com.tw/zh/products/office-workspace/office-desks-and-tables/mittzon-spr-99533396',
  },
  medium_meeting_chair: {
    name: 'Backbone Axo 人體工學辦公椅',
    url: 'https://www.backbone.tw/products/axo',
  },
  medium_meeting_tv: {
    name: 'Xiaomi 智慧顯示器 A 2026 65型 4K',
    url: 'https://www.mi.com/tw/product/xiaomi-tv-a-65-2026/',
  },
  small_meeting_table: {
    name: 'IKEA LAGKAPTEN/OLOV書桌/工作桌',
    url: 'https://www.ikea.com.tw/zh/products/home-workspace/home-desks/lagkapten-olov-spr-59417638',
  },
  small_meeting_chair: {
    name: 'Backbone Axo 人體工學辦公椅',
    url: 'https://www.backbone.tw/products/axo',
  },
  small_meeting_tv: {
    name: 'Xiaomi 智慧顯示器 A 2026 65型 4K',
    url: 'https://www.mi.com/tw/product/xiaomi-tv-a-65-2026/',
  },
};

// ========================================================================
// CostDetailModal 類別
// ========================================================================

export class CostDetailModal {
  private modal: Modal;
  private costData: CostBreakdown | null = null;
  private currentLevel: CostLevel = 'simple';

  constructor(_options: CostDetailModalOptions = {}) {
    this.modal = new Modal('費用明細', {
      size: 'full',  // 使用 full 尺寸以適應各種螢幕
      closeOnEscape: true,
      closeOnOverlay: true,
    });
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  /**
   * 開啟 Modal 並顯示指定等級的費用明細
   */
  open(costData: CostBreakdown, level: CostLevel = 'simple'): void {
    this.costData = costData;
    this.currentLevel = level;
    this.render();
    this.modal.open();
  }

  close(): void {
    this.modal.close();
  }

  destroy(): void {
    this.modal.destroy();
  }

  // ========================================================================
  // 私有方法
  // ========================================================================

  private render(): void {
    if (!this.costData) return;

    const body = this.modal.getBody();
    const levelData = this.costData[this.currentLevel];
    const levelName = this.getLevelName(this.currentLevel);

    body.innerHTML = `
      <div class="space-y-4 sm:space-y-6 px-2 sm:px-4 py-3">
        <!-- 等級標題 -->
        <div class="text-center">
          <h3 class="text-xl sm:text-2xl font-bold text-gray-900">${levelName}</h3>
        </div>

        <!-- 左右並排容器 - 在小螢幕上改為上下排列 -->
        <div class="flex flex-col xl:flex-row gap-4 sm:gap-6">
          <!-- 左側：規劃概要 -->
          <div class="xl:w-fit xl:shrink-0">
            <h4 class="text-base sm:text-lg font-semibold mb-3">規劃概要</h4>
            <div class="overflow-x-auto">
              <table class="w-full border-collapse text-sm min-w-[280px]">
                <thead class="bg-gray-100">
                  <tr>
                    <th class="py-2 px-3 sm:px-4 border text-left">項目</th>
                    <th class="py-2 px-3 sm:px-4 border text-left">數量</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr>
                    <td class="py-2 px-3 sm:px-4 border">房間坪數</td>
                    <td class="py-2 px-3 sm:px-4 border font-semibold">${this.costData.room_area_ping}</td>
                  </tr>
                  <tr>
                    <td class="py-2 px-3 sm:px-4 border">員工數</td>
                    <td class="py-2 px-3 sm:px-4 border font-semibold">${this.costData.desks_placed}</td>
                  </tr>
                  <tr>
                    <td class="py-2 px-3 sm:px-4 border">主管室數量</td>
                    <td class="py-2 px-3 sm:px-4 border font-semibold">${this.costData.supervisor_rooms_placed}</td>
                  </tr>
                  <tr>
                    <td class="py-2 px-3 sm:px-4 border">大會議室數量</td>
                    <td class="py-2 px-3 sm:px-4 border font-semibold">${this.costData.large_meeting_rooms_placed}</td>
                  </tr>
                  <tr>
                    <td class="py-2 px-3 sm:px-4 border">中會議室數量</td>
                    <td class="py-2 px-3 sm:px-4 border font-semibold">${this.costData.medium_meeting_rooms_placed}</td>
                  </tr>
                  <tr>
                    <td class="py-2 px-3 sm:px-4 border">小會議室數量</td>
                    <td class="py-2 px-3 sm:px-4 border font-semibold">${this.costData.small_meeting_rooms_placed}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 右側：費用明細表格 -->
          <div class="flex-1 min-w-0">
            <h4 class="text-base sm:text-lg font-semibold mb-3">費用明細</h4>
            <div class="overflow-x-auto -mx-2 sm:mx-0">
              <div class="inline-block min-w-full align-middle">
                <table class="min-w-full text-xs sm:text-sm border-collapse">
                  <thead class="bg-gray-100">
                    <tr>
                      <th class="py-2 px-2 sm:px-3 border text-left whitespace-nowrap">項目</th>
                      <th class="py-2 px-2 sm:px-3 border text-right whitespace-nowrap">數量</th>
                      <th class="py-2 px-2 sm:px-3 border text-right whitespace-nowrap">單價</th>
                      <th class="py-2 px-2 sm:px-3 border text-right whitespace-nowrap">總價</th>
                      <th class="py-2 px-2 sm:px-3 border text-left whitespace-nowrap hidden sm:table-cell">產品連結</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    ${this.renderCostItems(levelData)}
                  </tbody>
                  <tfoot class="font-bold bg-gray-50">
                    <tr class="border-t-2">
                      <td class="py-3 px-2 sm:px-3 border">總計</td>
                      <td class="py-3 px-2 sm:px-3 border text-right">-</td>
                      <td class="py-3 px-2 sm:px-3 border text-right">-</td>
                      <td class="py-3 px-2 sm:px-3 border text-right">${this.formatCurrency(levelData.total)}</td>
                      <td class="py-3 px-2 sm:px-3 border hidden sm:table-cell"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <!-- 小螢幕提示 -->
            <p class="text-xs text-gray-500 mt-2 sm:hidden">※ 可左右滑動查看完整表格</p>
          </div>
        </div>

        <!-- 操作按鈕 -->
        <div class="flex gap-3 justify-end pt-4 border-t">
          <button id="closeModalBtn" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            關閉
          </button>
        </div>
      </div>
    `;

    this.bindEvents(body);
  }

  private renderCostItems(levelData: CostBreakdownByLevel): string {
    const items: Array<{ name: string; key: keyof CostBreakdownByLevel; linkKey?: string }> = [
      { name: '裝潢費', key: 'renovation' },
      { name: '員工辦公桌', key: 'employee_desk', linkKey: this.getEmployeeLinkKey('desk') },
      { name: '員工辦公椅', key: 'employee_chair', linkKey: this.getEmployeeLinkKey('chair') },
      { name: '主管室桌', key: 'supervisor_desk', linkKey: 'supervisor_desk' },
      { name: '主管室椅', key: 'supervisor_chair', linkKey: 'supervisor_chair' },
      { name: '主管室電視', key: 'supervisor_tv', linkKey: 'supervisor_tv' },
      { name: '大會議室桌', key: 'large_meeting_table', linkKey: 'large_meeting_table' },
      { name: '大會議室椅', key: 'large_meeting_chair', linkKey: 'large_meeting_chair' },
      { name: '大會議室電視', key: 'large_meeting_tv', linkKey: 'large_meeting_tv' },
      { name: '中會議室桌', key: 'medium_meeting_table', linkKey: 'medium_meeting_table' },
      { name: '中會議室椅', key: 'medium_meeting_chair', linkKey: 'medium_meeting_chair' },
      { name: '中會議室電視', key: 'medium_meeting_tv', linkKey: 'medium_meeting_tv' },
      { name: '小會議室桌', key: 'small_meeting_table', linkKey: 'small_meeting_table' },
      { name: '小會議室椅', key: 'small_meeting_chair', linkKey: 'small_meeting_chair' },
      { name: '小會議室電視', key: 'small_meeting_tv', linkKey: 'small_meeting_tv' },
    ];

    return items
      .map((item) => {
        const itemData = levelData[item.key] as CostItemDetail;
        if (!itemData || itemData.quantity === 0) return '';

        const linkHtml = item.linkKey ? this.getProductLinkHtml(item.linkKey) : '';

        return `
          <tr>
            <td class="py-2 px-2 sm:px-3 border whitespace-nowrap">${item.name}</td>
            <td class="py-2 px-2 sm:px-3 border text-right whitespace-nowrap">${this.formatNumber(itemData.quantity)}</td>
            <td class="py-2 px-2 sm:px-3 border text-right whitespace-nowrap">${this.formatNumber(itemData.unit_price)}</td>
            <td class="py-2 px-2 sm:px-3 border text-right whitespace-nowrap">${this.formatNumber(itemData.total)}</td>
            <td class="py-2 px-2 sm:px-3 border hidden sm:table-cell">${linkHtml}</td>
          </tr>
        `;
      })
      .filter(Boolean)
      .join('');
  }

  private getEmployeeLinkKey(type: 'desk' | 'chair'): string {
    return this.currentLevel === 'premium'
      ? `employee_${type}_premium`
      : `employee_${type}_basic`;
  }

  /**
   * 🔒 轉義 HTML 屬性值（防止屬性破壞）
   */
  private escapeHtmlAttribute(attr: string): string {
    return attr
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private getProductLinkHtml(linkKey: string): string {
    const link = PRODUCT_LINKS[linkKey];
    if (!link) return '';

    // 🔒 安全性：轉義 URL 和名稱防止 XSS 和 HTML 注入
    const safeUrl = this.escapeHtmlAttribute(link.url);
    const safeName = escapeHtml(link.name);

    return `<a href="${safeUrl}" target="_blank" class="text-primary-600 hover:text-primary-800 underline">${safeName}</a>`;
  }

  private getLevelName(level: CostLevel): string {
    const names: Record<CostLevel, string> = {
      simple: '簡易版',
      standard: '標準版',
      premium: '高級版',
    };
    return names[level];
  }

  private formatNumber(num: number): string {
    return new Intl.NumberFormat('zh-TW').format(num);
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  private bindEvents(body: HTMLElement): void {
    const closeBtn = body.querySelector('#closeModalBtn');
    closeBtn?.addEventListener('click', () => this.close());
  }
}
